import { ViewportRuler } from '@angular/cdk/scrolling';
import { Injectable, NgZone } from '@angular/core';
import { ImproverConstants } from '@improver/app-config';
import { ResponsiveService } from '@improver/infrastructure/layout-data-layer';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { MarkedAsSeenTypes } from './settings.enum';

interface SettingsLayout {
  asideDisabled: boolean;
  genericModalOpenedCount: number;
  isAsideSidebarCollapsed: BehaviorSubject<boolean>;
  isCollapsedText: boolean;
  isFloat: boolean;
  theme: string;
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private layout: SettingsLayout = {
    asideDisabled: false,
    isAsideSidebarCollapsed: new BehaviorSubject(this.getSavedSidebarSettings()),
    genericModalOpenedCount: 0,
    isCollapsedText: false,
    isFloat: false,
    theme: null,
  };

  private resizeStreamSubscription: Subscription;

  constructor(
    private responsiveService: ResponsiveService,
    private viewport: ViewportRuler,
    private readonly ngZone: NgZone
  ) {}

  /**
   * @deprecated
   */
  setAsideCollapsedText(value: boolean) {
    this.layout.isCollapsedText = value;
  }

  toggleAsideSidebar(value: boolean = !this.layout.isAsideSidebarCollapsed.getValue()) {
    this.layout.isAsideSidebarCollapsed.next(value);
    this.setSidebarSettings(value);
    if (this.resizeStreamSubscription) {
      this.resizeStreamSubscription.unsubscribe();
    }
  }

  isAsideSidebarCollapsed(): Observable<boolean> {
    return this.layout.isAsideSidebarCollapsed.asObservable();
  }

  markAsSeen(type: MarkedAsSeenTypes) {
    localStorage.setItem(type, true.toString());
  }

  isMarkedAsSeen(type: MarkedAsSeenTypes): boolean {
    return Boolean(localStorage.getItem(type));
  }

  private getSavedSidebarSettings(): boolean {
    if (this.responsiveService.isMobile()) {
      return false;
    }

    const savedSettings = this.getSidebarSettingsFromStorage();

    if (savedSettings === null) {
      this.onScreenResize();
    }

    return savedSettings === null ? this.getDefaultSidebarSettingsBasedOnResolution() : savedSettings;
  }

  private getDefaultSidebarSettingsBasedOnResolution(): boolean {
    return this.responsiveService.isTabletResolution() ? true : false;
  }

  private getSidebarSettingsFromStorage(): boolean {
    return JSON.parse(localStorage.getItem(ImproverConstants.LOCAL_STORAGE.SIDEBAR_COLLAPSED));
  }

  private setSidebarSettings(collapsed: boolean) {
    localStorage.setItem(ImproverConstants.LOCAL_STORAGE.SIDEBAR_COLLAPSED, JSON.stringify(collapsed));
  }

  private onScreenResize() {
    this.resizeStreamSubscription = this.viewport
      .change(200)
      .pipe(
        map(() => this.getDefaultSidebarSettingsBasedOnResolution()),
        distinctUntilChanged()
      )
      .subscribe((shouldBeCollapsed: boolean) => {
        this.ngZone.run(() => {
          this.layout.isAsideSidebarCollapsed.next(shouldBeCollapsed);
        });
      });
  }
}
